import { SelectProps, Stack } from '@mui/material';
import {
  isKnownChain,
  OChainId,
  SUPPORTED_CHAINS,
  TChainId,
} from 'modules/api';
import { useConnection } from 'modules/auth';
import { useGlobalTranslation } from 'modules/i18n/hooks/useGlobalTranslation';
import { InputBase, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { useCallback } from 'react';
import { ChevronDownIcon } from 'modules/common/icons';
import { CircularProgress } from '@mui/material';
import { MenuItemContent } from './MenuItemContent';

export function NetworkSelect({
  sx = {},
}: Pick<SelectProps, 'sx'>): JSX.Element {
  const { t, keys: globalKeys } = useGlobalTranslation();

  const {
    chainId = OChainId.unsupported,
    switchNetwork,
    isSwitchNetworkLoading,
  } = useConnection();

  const handleNetworkChange = useCallback(
    (event: SelectChangeEvent<TChainId>) => {
      const selectedNetwork = event.target.value as TChainId;

      switchNetwork(selectedNetwork);
    },
    [switchNetwork],
  );

  return (
    <Select
      onChange={handleNetworkChange}
      size="small"
      input={<InputBase />}
      variant="outlined"
      IconComponent={ChevronDownIcon}
      value={chainId as TChainId}
      renderValue={selected => {
        if (isSwitchNetworkLoading) {
          return (
            <Stack alignItems="center" sx={{ minWidth: 80 }}>
              <CircularProgress size={16} />
            </Stack>
          );
        }

        const isKnown = isKnownChain(selected);

        return (
          <MenuItemContent
            text={t(
              globalKeys.chain[isKnown ? selected : OChainId.unsupported],
            )}
            chainId={selected}
            hasIcon={isKnown}
          />
        );
      }}
      MenuProps={{
        PaperProps: {
          sx: {
            backgroundColor: theme => theme.palette.customMain[900],
            border: '1px solid rgba(255, 255, 255, 0.10)',
          },
        },
      }}
      sx={{
        ...sx,
        '&& .MuiSelect-select': {
          maxWidth: 220,
          boxSizing: 'border-box',
          height: 40,
          alignContent: 'center',
          padding: theme => theme.spacing(1, 3, 1, 2),
          backgroundColor: '#131315',
          borderRadius: 12.5,
          transition: theme =>
            theme.transitions.create(['background-color'], {
              duration: theme.transitions.duration.short,
            }),
        },

        '&.Mui-focused, &:hover': {
          '& .MuiSelect-select': {
            backgroundColor: theme => theme.palette.customMain[550],
          },
        },

        '& .MuiSelect-icon': {
          height: 12,
          width: 12,
          top: 'calc(50% - 6px)',
          right: 12,
        },
      }}
    >
      {SUPPORTED_CHAINS.map(supportedChainId => (
        <MenuItem
          value={supportedChainId}
          key={supportedChainId}
          sx={{
            backgroundColor: theme => theme.palette.customMain[900],

            '&&.Mui-selected, &:hover, .Mui-selected:focus, &:focus': {
              backgroundColor: theme => theme.palette.brand.light,
            },
          }}
        >
          <MenuItemContent
            text={t(globalKeys.chain[supportedChainId])}
            chainId={supportedChainId}
            hasIcon
          />
        </MenuItem>
      ))}
    </Select>
  );
}
